/* ChangeProxyModal — re-source an account's proxy and force a fresh live login.
 *
 * OnlyFans/Fansly bind the live session to its IP, so a proxy change can't
 * silently overwrite accounts.proxy_url — it must be followed by a real
 * re-login. This overlay runs ProxyPicker(variant="change") -> LoginStream,
 * reusing the same streamed-login machinery as first connect.
 */
import { useState } from 'react';
import { ProxyPicker } from './ProxyPicker';
import { LoginStream } from './LoginStream';
import { C } from './onboardingKit';
import { normalizePlatform, type Platform } from '../PlatformLogo';

interface ChangeProxyModalProps {
  accountId: string;
  platform?: Platform;
  onClose: () => void;
  onConnected?: () => void;
}

const LABEL: Record<'onlyfans' | 'fansly', string> = { onlyfans: 'OnlyFans', fansly: 'Fansly' };

export function ChangeProxyModal({ accountId, platform, onClose, onConnected }: ChangeProxyModalProps) {
  const [phase, setPhase] = useState<'proxy' | 'login'>('proxy');
  const label = LABEL[normalizePlatform(platform)];

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'color-mix(in srgb, var(--om-page) 72%, transparent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: phase === 'login' ? 760 : 520, maxWidth: '100%', maxHeight: 'calc(92 * var(--om-vh))', overflowY: 'auto',
          background: 'var(--om-card)', border: `1px solid ${C.line2}`, borderRadius: 18, padding: 26,
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ color: C.text, fontSize: 20, fontWeight: 800, margin: 0 }}>
            {phase === 'proxy' ? 'Change proxy' : `Re-login to ${label}`}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: C.muted, fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <p style={{ color: C.muted, fontSize: 14, lineHeight: 1.5, margin: '0 0 18px' }}>
          {phase === 'proxy'
            ? `Pick a new proxy. Because ${label} binds the session to its IP, this triggers a fresh login through the new proxy.`
            : 'Sign in again below to complete the switch. Press Done once you’re on your account.'}
        </p>
        {phase === 'proxy' ? (
          <ProxyPicker
            accountId={accountId}
            variant="change"
            onSourced={() => setPhase('login')}
            onCancel={onClose}
          />
        ) : (
          <LoginStream
            accountId={accountId}
            onConnected={() => {
              onConnected?.();
              onClose();
            }}
          />
        )}
      </div>
    </div>
  );
}
